import React, { useEffect } from 'react';
import { ChevronRight, Download } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useCall from '@/hooks/useCall';
import useCalls from '@/hooks/useCalls';
import useScenarios from '@/hooks/useScenarios';
import Loading from '../Loading';
import Time from '../Time';
import DynamicCallScorecard from './DynamicCallScorecard';

const ScoreCard = () => {
  const navigate = useNavigate();
  const { callId } = useParams();

  const call = useCall(callId, {
    refetchInterval: (data: any) => {
      if (data && (!data.transcript || !data.score)) {
        const createdAt = Date.parse(data.created_at);
        const now = Date.now();
        const elapsed = now - createdAt;
        if (elapsed > 5 * 60 * 1000) {
          // More than 5 minutes have passed
          return false; // Stop refetching
        } else {
          return 5000; // Continue refetching every 5 seconds
        }
      }
      return false; // Stop refetching
    },
  });

  const calls = useCalls();
  const scenarios = useScenarios();

  const last5CallsOfMine =
    calls?.data?.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)).slice(0, 5) || [];
  const sortedAgents = scenarios?.data?.sort((a, b) => a.name.localeCompare(b.name)) || [];

  const generateEmotion = (entry: any) => {
    if (entry.emotion) {
      switch (entry.emotion[0]) {
        case 'sad':
          return '😢';
        case 'neu':
          return '😃';
        case 'ang':
          return '😡';
        default:
          return '';
      }
    }
    return '';
  };

  const createdAt = call.data ? Date.parse(call.data.created_at) : null;
  const now = Date.now();
  const elapsed = createdAt ? now - createdAt : null;
  const isTimedOut = elapsed && elapsed > 5 * 60 * 1000 && (!call.data?.transcript || !call.data?.score);

  useEffect(() => {
    if (call.data?.score) {
      console.log('Score updated:', call.data.score);
    }
  }, [call.data?.score]);

  return (
    <div>
      {call.isLoading || !call.data ? (
        <Loading />
      ) : (
        <>
          <div className="px-4 py-6 sm:px-0">
            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="w-full space-y-6 lg:w-1/4">
                <Card>
                  <CardHeader>
                    <CardTitle>Scenarios</CardTitle>
                    <CardDescription>Choose a scenario type to practice</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Select onValueChange={(value) => navigate(`/scenarios/${value}`)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select scenario type" />
                        </SelectTrigger>
                        <SelectContent>
                          {sortedAgents.map((agent) => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Calls</CardTitle>
                    <CardDescription>Select a call to view details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {last5CallsOfMine.map((call) => (
                      <Button variant={'link'} key={call.id} asChild className="w-full mb-4">
                        <Link to={`/call_scenarios/${call.id}`}>
                          <div className="flex items-center justify-between w-full">
                            <div>
                              <p className="font-semibold">Call</p>
                              <p className="text-sm text-gray-500">
                                <Time>{call.created_at}</Time>
                              </p>
                            </div>
                            <div className="flex items-center">
                              <ChevronRight className="w-5 h-5 ml-2" />
                            </div>
                          </div>
                        </Link>
                      </Button>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <Card className="w-full lg:w-3/5">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Call Transcript</CardTitle>

                      {call.data && (
                        <CardDescription>
                          <div>
                            Date: <Time>{call.data.created_at}</Time>
                          </div>
                          <div>Caller: {call.data.user?.name}</div>
                          <div>Duration: {call.data.duration} seconds</div>
                        </CardDescription>
                      )}
                    </div>
                    {call.data.audio_file_url && (
                      <Button variant="outline" size="sm" asChild disabled={call.data.audio_file_url === ''}>
                        <Link to={call.data.audio_file_url} target="_blank">
                          <Download className="w-4 h-4 mr-2" /> Download
                        </Link>
                      </Button>
                    )}
                  </div>
                  {call.data?.audio_file_url != '' && (
                    <audio controls>
                      <source src={call.data.audio_file_url} type="audio/mp3" />
                      Your browser does not support the audio element.
                    </audio>
                  )}
                </CardHeader>
                <CardContent>
                  {call.data.transcript && call.data.transcript.length > 0 ? (
                    <div className="space-y-4">
                      {call.data.transcript.map((entry: any, index: number) => (
                        <div
                          key={index}
                          className={`flex ${entry.speaker === 'SPEAKER_00' ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[80%] ${
                              entry.speaker === 'SPEAKER_00' ? 'bg-blue-100' : 'bg-green-100'
                            } rounded-lg p-3`}
                          >
                            <div className="flex items-center justify-between gap-4 mb-1">
                              <span className="text-sm font-semibold">
                                {entry.speaker} {generateEmotion(entry)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {entry.start.toString().padStart(2, '0')}:{entry.end.toString().padStart(2, '0')}
                              </span>
                            </div>
                            <p className="text-sm">{entry.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : isTimedOut ? (
                    <div className="flex items-center justify-center w-full h-40">
                      <div className="flex flex-col items-center">
                        <p className="text-sm text-gray-500">There may have been a problem. Please try again.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-full h-40">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 mb-2 border-t-2 border-b-2 border-blue-800 rounded-full animate-spin"></div>
                        <p className="text-sm text-gray-500">awaiting post call processing...</p>
                        <p className="text-[12px] text-gray-500">Takes a couple minutes</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              <div className="w-full space-y-6 lg:w-1/3">
                <Card>
                  <CardHeader>
                    <CardTitle>Summary</CardTitle>
                    <CardDescription>Review of the call</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {call.data.summary ? (
                      <p>{call.data.summary}</p>
                    ) : isTimedOut ? (
                      <div className="flex items-center justify-center w-full h-40">
                        <div className="flex flex-col items-center">
                          <p className="text-sm text-gray-500">There may have been a problem. Please try again.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-full h-40">
                        <div className="flex flex-col items-center">
                          <p className="text-sm text-gray-500">awaiting post call processing...</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {call.data.score ? (
                  <DynamicCallScorecard data={call.data.score} />
                ) : isTimedOut ? (
                  <Card>
                    <CardHeader>
                      <CardTitle>Call Scorecard</CardTitle>
                      <CardDescription>Comprehensive performance analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-center w-full h-40">
                        <div className="flex flex-col items-center">
                          <p className="text-sm text-gray-500">There may have been a problem. Please try again.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle>Call Scorecard</CardTitle>
                      <CardDescription>Comprehensive performance analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-center w-full h-40">
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 mb-2 border-t-2 border-b-2 border-blue-800 rounded-full animate-spin"></div>
                          <p className="text-sm text-gray-500">awaiting post call processing...</p>
                          <p className="text-[12px] text-gray-500">Takes a couple minutes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ScoreCard;
